import React, {
  useCallback,
  useRef,
  KeyboardEvent,
  ChangeEvent,
  useState,
  useEffect,
  useImperativeHandle,
} from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface TimeInputProps {
  label: string;
  value: number;
  name?: string;
  onChange: (value: number, event: React.SyntheticEvent<HTMLInputElement>) => void;
  max?: number;
  min?: number;
  disabled?: boolean;
  onArrowLeft?: () => void;
  onArrowRight?: () => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  className?: string;
}

export interface TimeInputRef {
  triggerShake: () => void;
  focus: () => void;
}

export const TimeInput = ({
  label,
  value,
  onChange,
  max = 365,
  min = 0,
  disabled,
  onArrowLeft,
  onArrowRight,
  onBlur,
  className,
  ref,
}: TimeInputProps & { ref?: React.Ref<TimeInputRef> }) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = internalRef;
  const [shouldShake, setShouldShake] = useState(false);

  // Format value to always show 2 digits
  const formattedValue = value.toString().padStart(2, '0');
  const shakeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Trigger shake animation
  const triggerShake = useCallback(() => {
    setShouldShake(true);
    // Clear any existing timeout
    if (shakeTimeoutRef.current) {
      clearTimeout(shakeTimeoutRef.current);
    }
    // Set timeout to reset shake after animation completes
    shakeTimeoutRef.current = setTimeout(() => {
      setShouldShake(false);
      shakeTimeoutRef.current = null;
    }, 500);
  }, []);

  // Expose triggerShake and focus methods via ref
  useImperativeHandle(ref, () => ({
    triggerShake,
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.replace(/\D/g, ''); // Only allow numbers
      if (newValue === '') {
        onChange(0, e);
        return;
      }

      let numericValue = parseInt(newValue, 10);
      const originalValue = numericValue;

      // Don't enforce min during typing to allow typing "10" when min is 5
      // Only cap at max value during typing
      if (numericValue > max) {
        numericValue = max;
        if (originalValue > max) {
          triggerShake();
        }
      }

      onChange(numericValue, e);
    },
    [max, onChange, triggerShake]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/\D/g, '');

      if (inputValue === '') {
        onChange(min, e);
        onBlur?.(e);
        return;
      }

      let numericValue = parseInt(inputValue, 10);
      const originalValue = numericValue;

      // Validate and correct on blur
      if (numericValue < min) {
        numericValue = min;
        if (originalValue < min) {
          triggerShake();
        }
      } else if (numericValue > max) {
        numericValue = max;
        if (originalValue > max) {
          triggerShake();
        }
      }

      onChange(numericValue, e);
      onBlur?.(e);
    },
    [min, max, onChange, onBlur, triggerShake]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      switch (e.key) {
        case 'ArrowUp': {
          e.preventDefault();
          const newValueUp = Math.min(value + 1, max);
          if (value >= max && newValueUp === max) {
            triggerShake();
          }
          onChange(newValueUp, e);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const newValueDown = Math.max(value - 1, min);
          if (value <= min && newValueDown === min) {
            triggerShake();
          }
          onChange(newValueDown, e);
          break;
        }
        case 'ArrowLeft':
          // If cursor is at the start, move to previous field
          if (input.selectionStart === 0 && onArrowLeft) {
            e.preventDefault();
            onArrowLeft();
          }
          break;
        case 'ArrowRight':
          // If cursor is at the end, move to next field
          if (input.selectionStart === input.value.length && onArrowRight) {
            e.preventDefault();
            onArrowRight();
          }
          break;
      }
    },
    [value, max, min, onChange, onArrowLeft, onArrowRight, inputRef, triggerShake]
  );

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  }, []);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label className="text-sm leading-5 font-medium text-foreground">{label}</label>
      <Input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={formattedValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        className={cn('h-10 w-11 text-center', shouldShake && 'animate-shake')}
      />
    </div>
  );
};
